import type { StatusVariant } from "@/lib/colors";
import { DEVICE_ROLE_SECTIONS } from "./device-role-config";
import type {
	ConnectionPolicy,
	DeviceAgent,
	DeviceDiagnostics,
	DeviceStatus,
	MetricsTriple,
} from "./devices-api";

/**
 * Pure, render-free helpers for the tiles launcher: grouping by location,
 * search filtering, and diagnostics formatting. Kept separate so they can be
 * unit-tested without mounting the React tree.
 */

export interface DeviceGroup {
	/** Stable key — the idfirm, or "none" for unassigned devices. */
	key: string;
	/** Human label for the section header. */
	label: string;
	/** null only for the "Inne / nieprzypisane" bucket. */
	idfirm: number | null;
	devices: DeviceAgent[];
}

const UNASSIGNED_LABEL = "Inne / nieprzypisane";

/**
 * Scala mapę nazw aptek ze `/scope/tree` z nazwami przyniesionymi wprost przez
 * urządzenia (`device.pharmacyName`). Apteki udostępnione przez delegację są
 * poza własnym drzewem widoczności, więc `/scope/tree` ich nie zna — bez tego
 * pokazywałyby się jako „Apteka <idfirm>". Drzewo ma pierwszeństwo (spójność z
 * resztą UI); nazwa z urządzenia tylko uzupełnia braki.
 */
export function mergePharmacyNames(
	base: Map<number, string>,
	devices: DeviceAgent[],
): Map<number, string> {
	const map = new Map(base);
	for (const d of devices) {
		if (d.idfirm != null && d.pharmacyName && !map.has(d.idfirm)) {
			map.set(d.idfirm, d.pharmacyName);
		}
	}
	return map;
}

/** Etykieta apteki urzadzenia w banerach: nazwa z mapy, "Apteka <idfirm>" albo etykieta urzadzenia. */
export function pharmacyLabelFor(device: DeviceAgent, pharmacyNames: Map<number, string>): string {
	return device.idfirm != null
		? (pharmacyNames.get(device.idfirm) ?? `Apteka ${device.idfirm}`)
		: deviceLabel(device);
}

/** Tozsamosc apteki w banerach (dwie apteki moga miec te sama nazwe); bez apteki — urzadzenie. */
export function pharmacyKey(device: DeviceAgent): string {
	return device.idfirm != null ? `${device.tenantId ?? "-"}:${device.idfirm}` : device.deviceUuid;
}

/** Sort comparator: devices with "serwer" in name come first, rest alphabetically by display name. */
function compareDevicesServerFirst(a: DeviceAgent, b: DeviceAgent): number {
	const aLabel = deviceLabel(a).toLowerCase();
	const bLabel = deviceLabel(b).toLowerCase();
	const aHasSerwer = aLabel.includes("serwer");
	const bHasSerwer = bLabel.includes("serwer");
	if (aHasSerwer !== bHasSerwer) {
		return aHasSerwer ? -1 : 1;
	}
	return aLabel.localeCompare(bLabel, "pl");
}

/**
 * Group devices by pharmacy (idfirm). Devices without an idfirm land in a
 * single "Inne / nieprzypisane" bucket which always sorts last. Groups are
 * ordered by idfirm ascending; devices inside a group by display name.
 */
export function groupDevicesByLocation(
	devices: DeviceAgent[],
	resolvePharmacyName?: (idfirm: number) => string | undefined,
): DeviceGroup[] {
	const buckets = new Map<string, DeviceGroup>();

	for (const device of devices) {
		const key = device.idfirm == null ? "none" : String(device.idfirm);
		let bucket = buckets.get(key);
		if (!bucket) {
			// Etykieta = nazwa apteki (z farm_firm via /scope/tree); fallback na
			// "Apteka <idfirm>" gdy nazwy brak (drzewo niezaladowane / apteka poza
			// widocznoscia). KS-id ksiegowej nic nie mowi — nazwa tak.
			const label =
				device.idfirm == null
					? UNASSIGNED_LABEL
					: (resolvePharmacyName?.(device.idfirm) ?? `Apteka ${device.idfirm}`);
			bucket = {
				key,
				idfirm: device.idfirm,
				label,
				devices: [],
			};
			buckets.set(key, bucket);
		}
		bucket.devices.push(device);
	}

	const groups = [...buckets.values()];
	for (const group of groups) {
		group.devices.sort(compareDevicesServerFirst);
	}

	groups.sort((a, b) => {
		// Unassigned bucket always last.
		if (a.idfirm == null) return 1;
		if (b.idfirm == null) return -1;
		return a.idfirm - b.idfirm;
	});

	return groups;
}

export interface DeviceRoleSection {
	/** Section key — device role or `other`. */
	key: string;
	/** Section heading in the tiles launcher. */
	label: string;
	/** When true, render pharmacy sub-headers inside the section. */
	subGroupByPharmacy: boolean;
	groups: DeviceGroup[];
	deviceCount: number;
}

/**
 * Group devices by role section (Apteki, Serwery, Laptopy, Natubay, IT).
 * Only `pharmacy_workstation` is further split by apteka inside its section.
 * Unknown roles land in „Inne” at the end.
 */
export function groupDevicesByRole(
	devices: DeviceAgent[],
	resolvePharmacyName?: (idfirm: number) => string | undefined,
): DeviceRoleSection[] {
	const knownRoles = new Set<string>(DEVICE_ROLE_SECTIONS.map((s) => s.role));
	const sections: DeviceRoleSection[] = [];

	for (const def of DEVICE_ROLE_SECTIONS) {
		if (def.role === "other") continue;
		const roleDevices = devices.filter((d) => d.deviceRole === def.role);
		if (roleDevices.length === 0) continue;

		const groups = def.subGroupByPharmacy
			? groupDevicesByLocation(roleDevices, resolvePharmacyName)
			: [
					{
						key: def.role,
						label: "",
						idfirm: null,
						devices: [...roleDevices].sort(compareDevicesServerFirst),
					},
				];

		sections.push({
			key: def.role,
			label: def.label,
			subGroupByPharmacy: def.subGroupByPharmacy,
			groups,
			deviceCount: roleDevices.length,
		});
	}

	const otherDevices = devices.filter((d) => !knownRoles.has(d.deviceRole));
	if (otherDevices.length > 0) {
		sections.push({
			key: "other",
			label: "Inne",
			subGroupByPharmacy: false,
			groups: [
				{
					key: "other",
					label: "",
					idfirm: null,
					devices: [...otherDevices].sort(compareDevicesServerFirst),
				},
			],
			deviceCount: otherDevices.length,
		});
	}

	return sections;
}

export interface ConnectionPolicyInfo {
	/** Short badge/label text. */
	label: string;
	/** One-line explanation of what the policy means for the operator. */
	hint: string;
	/** True when the user at the device must accept the connection first. */
	requiresConfirm: boolean;
}

/**
 * Map a resolved connection policy to Polish UI copy. Pure so it can be
 * unit-tested and reused by both the collapsed badge and the expanded control.
 */
export function connectionPolicyLabel(effective: ConnectionPolicy): ConnectionPolicyInfo {
	if (effective === "confirm") {
		return {
			label: "Wymaga potwierdzenia",
			hint: "Użytkownik przy urządzeniu musi zaakceptować",
			requiresConfirm: true,
		};
	}
	return {
		label: "Bez potwierdzenia",
		hint: "Apteka łączy się bez pytania",
		requiresConfirm: false,
	};
}

/** Best human-readable label for a device, with sensible fallbacks. */
export function deviceLabel(device: DeviceAgent): string {
	return device.displayName ?? device.hostname ?? device.deviceUuid;
}

/**
 * Stanowisko "serwerowe" apteki = nazwa zawiera "serwer" (np. "ST3 Serwer/Biuro").
 * Tylko grupa "Apteki" (pharmacy_workstation) — franczyza/biura/IT maja
 * inny wlasciciel i inne priorytety, nie mieszamy ich w alertach serwerowych.
 */
export function isPharmacyServerWorkstation(device: DeviceAgent): boolean {
	return (
		device.deviceRole === "pharmacy_workstation" &&
		deviceLabel(device).toLowerCase().includes("serwer")
	);
}

/** Minuty od `iso` do `now`. `null` gdy brak/niepoprawnej daty. */
export function minutesSince(iso: string | null | undefined, now: Date): number | null {
	if (!iso) return null;
	const at = new Date(iso);
	if (Number.isNaN(at.getTime())) return null;
	return Math.max(0, Math.floor((now.getTime() - at.getTime()) / 60_000));
}

/** "2 godz 15 min" / "15 min" — z minutami, zeby krotki czas (np. 15 min) nie znikal jako "0 godz". */
export function formatMinutesDuration(totalMinutes: number): string {
	const hours = Math.floor(totalMinutes / 60);
	const mins = totalMinutes % 60;
	return hours > 0 ? `${hours} godz ${mins} min` : `${mins} min`;
}

/** enrollment_state value that marks a device as awaiting admin approval. */
export const PENDING_ENROLLMENT_STATE = "enrolled_unassigned";

/** Fast poll cadence (ms) while a device is mid-onboarding/provisioning. */
export const DEVICES_PROVISIONING_POLL_MS = 5_000;
/**
 * Steady background refresh (ms) for the live „Zdalny dostęp" view when nothing
 * is actively changing. Low enough that a brand-new enrollment (which is not yet
 * in the list, so only a refetch can surface it) appears on its own within
 * seconds instead of forcing the operator to hit F5.
 */
export const DEVICES_IDLE_POLL_MS = 15_000;

/**
 * Is this device in a state the operator is actively watching change — so the
 * list should poll fast? Two such states:
 *  - awaiting admin approval (`enrolled_unassigned`): the approval card should
 *    appear without an F5;
 *  - online but no RustDesk ID yet: startup provisioning is still in flight and
 *    the ID lands within tens of seconds.
 * Pure so the refetch policy is unit-testable.
 */
export function isDeviceProvisioning(
	device: Pick<DeviceAgent, "hubOnline" | "rustdeskId" | "enrollmentState">,
): boolean {
	if (device.enrollmentState === PENDING_ENROLLMENT_STATE) return true;
	const hasId = device.rustdeskId != null && device.rustdeskId.trim() !== "";
	return device.hubOnline === true && !hasId;
}

/**
 * Refetch cadence for the devices list: fast while any device is provisioning or
 * awaiting approval, otherwise the idle cadence (which still catches new
 * enrollments that are not yet in the list). Never returns `false` — the view is
 * a live operational dashboard and must not silently freeze.
 */
export function devicesRefetchInterval(devices: DeviceAgent[] | undefined): number {
	if (devices?.some(isDeviceProvisioning)) return DEVICES_PROVISIONING_POLL_MS;
	return DEVICES_IDLE_POLL_MS;
}

/**
 * Can we connect to this device RIGHT NOW? The hub (live WS presence) is the
 * single source of truth — it is exactly what the remote-session gate checks.
 * The `status` column lags: it stays "online" during the disconnect grace
 * period and is stale right after a backend restart (empty hub). Driving the
 * UI off `status` promises a connection that then 409s — drive it off this.
 */
export function isDeviceReachable(device: Pick<DeviceAgent, "hubOnline">): boolean {
	return device.hubOnline === true;
}

/**
 * Cel offline, ale do wydzwonienia bez agenta: usługa FarmasterRustDesk działa
 * niezależnie od agenta ze stałym ID i hasłem z wiersza urządzenia (polityka
 * `unattended`). Backend w tej sytuacji pomija handshake `prepare_remote` i
 * odpala kontroler od razu (`launchFallback: target_offline_direct`). Tylko dla
 * super_admina — to droga ratunkowa (restart zawieszonego agenta, incydent
 * 2026-08-26), nie codzienny przycisk.
 */
export function canConnectOfflineDirect(
	device: Pick<DeviceAgent, "hubOnline" | "rustdeskId" | "effectiveConnectionPolicy">,
	isSuperAdmin: boolean,
): boolean {
	return (
		isSuperAdmin &&
		device.hubOnline !== true &&
		!!device.rustdeskId &&
		device.effectiveConnectionPolicy === "unattended"
	);
}

/** NASZ self-hosted serwer RustDesk — te same wartości co `DEFAULT_RUSTDESK_SERVER_*`
 *  agenta. Klucz jest PUBLICZNY (rozdawany każdemu klientowi), nie sekret. */
export const RUSTDESK_SERVER_HOST = "rust.farmaster.pl";
export const RUSTDESK_SERVER_KEY = "LyQi7EbqETYAVjfQg7x5bKH6xVntOZHp0sCSxwGqlAw=";

/**
 * Czy przeglądarka działa na Windows? Pełny agent i Lite są budowane WYŁĄCZNIE
 * na Windows — na Macu/Linuksie ich schematy (`farmaster-agent://`,
 * `farmaster://`) nie są zarejestrowane, a Safari pokazuje tylko błąd adresu.
 */
export function isWindowsClient(userAgent: string = navigator.userAgent): boolean {
	return /Windows/i.test(userAgent);
}

/**
 * Link otwierający STOCKOWY klient RustDesk (Mac/Linux, bez agenta) prosto na
 * stanowisko. `<id>@<serwer>?key=` kieruje na nasz serwer TYLKO to połączenie —
 * ustawienia klienta zostają nietknięte (publiczny RustDesk dalej działa).
 * Zweryfikowane z kodem RustDeska: `urlLinkToCmdArgs` (flutter/lib/common.dart)
 * zamienia link na `--connect <id>@<serwer>?key=… --password …`, a
 * `LoginConfigHandler::initialize` (src/client.rs) ustawia z niego `other_server`.
 * Bez hasła (polityka `confirm`) połączenie akceptuje osoba przy stanowisku.
 */
export function buildRustdeskDirectLink(rustdeskId: string, password: string | null): string {
	const link = `rustdesk://connect/${encodeURIComponent(rustdeskId.trim())}@${RUSTDESK_SERVER_HOST}?key=${encodeURIComponent(RUSTDESK_SERVER_KEY)}`;
	return password ? `${link}&password=${encodeURIComponent(password)}` : link;
}

/** Segment wersji jak `str::parse::<u64>().unwrap_or(0)` w backendzie: ścisły, śmieci = 0. */
function versionSegment(raw: string): number {
	const s = raw.trim();
	return /^\+?\d+$/.test(s) ? Number(s) : 0;
}

/**
 * Czy wersja agenta `current` jest STARSZA od `target`. Lustro backendowego
 * `version_lt` (`services/device_update.rs`) — to on decyduje, czy kampania i
 * pojedyncza aktualizacja w ogóle ruszą urządzenie, więc przycisk musi liczyć
 * identycznie: segmenty po `.`, brakujące = 0, nienumeryczne = 0. Brak którejś
 * wersji → `false` (w wątpliwości niczego nie proponujemy).
 */
export function isAgentVersionOlder(
	current: string | null | undefined,
	target: string | null | undefined,
): boolean {
	if (!current?.trim() || !target?.trim()) return false;
	const c = current.trim().split(".").map(versionSegment);
	const t = target.trim().split(".").map(versionSegment);
	const n = Math.max(c.length, t.length);
	for (let i = 0; i < n; i++) {
		const cv = c[i] ?? 0;
		const tv = t[i] ?? 0;
		if (cv !== tv) return cv < tv;
	}
	return false;
}

/**
 * Status to display: the hub is authoritative for reachability. A device the DB
 * still calls "online" but with no live hub connection is downgraded to
 * "offline" so the badge never claims an unreachable device is online.
 */
export function effectiveDeviceStatus(
	device: Pick<DeviceAgent, "status" | "hubOnline">,
): DeviceStatus {
	if (device.hubOnline) return "online";
	if (device.status === "online") return "offline";
	return device.status;
}

/**
 * Case-insensitive substring match across the fields a user would type:
 * display name, hostname, RustDesk ID, idfirm i — gdy podano resolver — pełna
 * nazwa apteki (z `farm_firm`, np. "Apteka PRIMA … Kraków"). Bez resolvera
 * szukanie po mieście/nazwie apteki nie działa, bo lista trzyma tylko idfirm.
 * Pusty query zwraca wszystko.
 */
export function filterDevices(
	devices: DeviceAgent[],
	query: string,
	resolvePharmacyName?: (idfirm: number) => string | undefined,
): DeviceAgent[] {
	const q = query.trim().toLowerCase();
	if (!q) return devices;
	return devices.filter((device) => {
		const haystack = [
			device.displayName,
			device.hostname,
			device.deviceUuid,
			device.rustdeskId,
			device.idfirm == null ? null : String(device.idfirm),
			device.idfirm == null ? null : (resolvePharmacyName?.(device.idfirm) ?? null),
		]
			.filter((v): v is string => !!v)
			.join(" ")
			.toLowerCase();
		return haystack.includes(q);
	});
}

/** Round a byte/GiB number to one decimal for display. */
function oneDecimal(value: number): number {
	return Math.round(value * 10) / 10;
}

/** "12 dni 3 godz", "5 godz 20 min", "14 min" — coarse human uptime. */
export function formatUptime(uptimeSec: number | null | undefined): string {
	if (uptimeSec == null || !Number.isFinite(uptimeSec) || uptimeSec < 0) return "—";
	const totalMin = Math.floor(uptimeSec / 60);
	const days = Math.floor(totalMin / (60 * 24));
	const hours = Math.floor((totalMin % (60 * 24)) / 60);
	const mins = totalMin % 60;
	if (days > 0) return `${days} dni ${hours} godz`;
	if (hours > 0) return `${hours} godz ${mins} min`;
	return `${mins} min`;
}

export interface LastRebootInfo {
	/** Whole days since the last real boot, or null when unknown. */
	days: number | null;
	/** "Ostatni pełny restart: N dni temu" / "dzisiaj" / "brak danych". */
	label: string;
	/**
	 * True when bootType indicates Windows fast-startup (1) — the box never
	 * really powered the kernel down, so "restart" is misleading.
	 */
	fastStartup: boolean;
	/** Hint to surface when fastStartup is true. */
	hint: string | null;
}

/**
 * Derive "last full reboot" from lastBootAt + bootType. bootType 1 means
 * Windows fast-startup (hybrid shutdown) — it was NOT a real cold boot, so we
 * flag it. `now` is injectable for deterministic tests.
 */
export function formatLastReboot(
	lastBootAt: string | null | undefined,
	bootType: number | null | undefined,
	now: Date = new Date(),
): LastRebootInfo {
	const fastStartup = bootType === 1;
	const hint = fastStartup
		? "Szybki rozruch (fast-startup) — to nie był pełny restart systemu."
		: null;

	if (!lastBootAt) {
		return { days: null, label: "Ostatni pełny restart: brak danych", fastStartup, hint };
	}
	const boot = new Date(lastBootAt);
	if (Number.isNaN(boot.getTime())) {
		return { days: null, label: "Ostatni pełny restart: brak danych", fastStartup, hint };
	}

	const ms = now.getTime() - boot.getTime();
	const days = Math.max(0, Math.floor(ms / (1000 * 60 * 60 * 24)));
	const label =
		days === 0
			? "Ostatni pełny restart: dzisiaj"
			: days === 1
				? "Ostatni pełny restart: wczoraj"
				: `Ostatni pełny restart: ${days} dni temu`;
	return { days, label, fastStartup, hint };
}

export interface DiskAlert {
	freeGb: number | null;
	totalGb: number | null;
	freePct: number | null;
	usedGb: number | null;
	/** Occupied share of C: (0–100). Drives the progress bar. */
	usedPct: number | null;
	/** True when free space is critically low (<10%). Never true when unknown. */
	low: boolean;
	/** False when the agent could not read disk stats (all fields null). */
	hasData: boolean;
	/** "323.4 / 464.8 GB zajęte (69.6%)" or "brak danych". */
	label: string;
}

const LOW_DISK_PCT = 10;

/**
 * Format disk C: usage + flag the low-space alert (<10% free). The agent sends
 * null disk fields when statvfs/GetDiskFreeSpaceExW fails — never render those
 * as "0 / 0 GB (0%)" with a false red alert. A null total or pct means "brak
 * danych" with low=false; the alert only fires on a finite freePct < 10.
 */
export function formatDiskAlert(
	diag: Pick<DeviceDiagnostics, "diskCFreeGb" | "diskCTotalGb" | "diskCFreePct">,
): DiskAlert {
	if (
		diag.diskCTotalGb == null ||
		diag.diskCFreePct == null ||
		!Number.isFinite(diag.diskCTotalGb) ||
		!Number.isFinite(diag.diskCFreePct)
	) {
		return {
			freeGb: diag.diskCFreeGb == null ? null : oneDecimal(diag.diskCFreeGb),
			totalGb: diag.diskCTotalGb == null ? null : oneDecimal(diag.diskCTotalGb),
			freePct: diag.diskCFreePct == null ? null : oneDecimal(diag.diskCFreePct),
			usedGb: null,
			usedPct: null,
			low: false,
			hasData: false,
			label: "brak danych",
		};
	}

	const freeGb = diag.diskCFreeGb == null ? null : oneDecimal(diag.diskCFreeGb);
	const totalGb = oneDecimal(diag.diskCTotalGb);
	const freePct = oneDecimal(diag.diskCFreePct);
	const usedPct = oneDecimal(100 - freePct);
	const usedGb =
		freeGb == null ? oneDecimal(totalGb - (diag.diskCFreeGb ?? 0)) : oneDecimal(totalGb - freeGb);
	return {
		freeGb,
		totalGb,
		freePct,
		usedGb,
		usedPct,
		low: freePct < LOW_DISK_PCT,
		hasData: true,
		label: `${usedGb} / ${totalGb} GB zajęte (${usedPct}%)`,
	};
}

/** Hint shown under uptime when Windows fast-startup skews boot-time semantics. */
export function formatBootTypeHint(bootType: number | null | undefined): string | null {
	if (bootType === 1) {
		return "Szybki rozruch (fast-startup) — czas pracy może nie odzwierciedlać pełnego restartu.";
	}
	return null;
}

/** Human label for the print spooler queue depth. */
export function formatPrinterQueueCount(count: number | null | undefined): string {
	if (count == null || !Number.isFinite(count) || count < 0) return "brak danych";
	if (count === 0) return "0 dokumentów w buforze";
	if (count === 1) return "1 dokument w buforze";
	return `${count} dokumentów w buforze`;
}

/**
 * Stan usługi Windows naszego klienta RustDesk, przetłumaczony na kropkę w
 * panelu: zielona = zdalny dostęp na hoście żyje, żółta = usługa stoi albo
 * właśnie się instaluje (stan przejściowy, sam się naprawi), czerwona = usługi
 * nie ma. `null` = agent starszy niż 0.1.101 albo nie-Windows — wtedy nie
 * udajemy wiedzy i pokazujemy szarą kropkę „brak danych".
 * `unknown` (agent 0.1.113+) = `sc query` padło albo zawisło (limit czasu):
 * stanu nie znamy, ale host coś zgłosił — żółta, jak `other`.
 *
 * `reinstalling` (świeżo zlecona czystka) wygrywa nad surowym stanem: brak
 * usługi w trakcie reinstalacji jest OCZEKIWANY i nie może straszyć czerwienią.
 */
export function formatRustdeskServiceState(
	state: string | null | undefined,
	reinstalling = false,
): { label: string; variant: StatusVariant } | null {
	if (reinstalling) return { label: "instalacja w toku", variant: "warning" };
	switch (state) {
		case "running":
			return { label: "działa", variant: "success" };
		case "starting":
			return { label: "uruchamia się", variant: "warning" };
		case "stopped":
			return { label: "zatrzymana", variant: "warning" };
		case "absent":
			return { label: "wyłączona (brak usługi)", variant: "error" };
		case "unknown":
			return { label: "nie odpowiada (sc query)", variant: "warning" };
		case "other":
			return { label: "stan nieznany", variant: "warning" };
		default:
			return null;
	}
}

/**
 * Human "freshness" stamp for cached diagnostics — "sprzed 3 godz" / "sprzed
 * 2 dni". `now` injectable for tests.
 */
export function formatCacheAge(collectedAt: string | null, now: Date = new Date()): string {
	if (!collectedAt) return "brak daty";
	const at = new Date(collectedAt);
	if (Number.isNaN(at.getTime())) return "brak daty";
	const ms = Math.max(0, now.getTime() - at.getTime());
	const mins = Math.floor(ms / (1000 * 60));
	const hours = Math.floor(mins / 60);
	const days = Math.floor(hours / 24);
	if (days >= 1) return `sprzed ${days} dni`;
	if (hours >= 1) return `sprzed ${hours} godz`;
	if (mins >= 1) return `sprzed ${mins} min`;
	return "przed chwilą";
}

/** Human file size for the installer metadata (1 decimal, MB/KB/B). */
export function formatBytes(bytes: number | null | undefined): string {
	if (bytes == null || !Number.isFinite(bytes) || bytes < 0) return "—";
	if (bytes < 1024) return `${bytes} B`;
	const kb = bytes / 1024;
	if (kb < 1024) return `${kb.toFixed(1)} KB`;
	const mb = kb / 1024;
	if (mb < 1024) return `${mb.toFixed(1)} MB`;
	return `${(mb / 1024).toFixed(1)} GB`;
}

/**
 * Short Polish label for how long an enrollment token stays valid (e.g.
 * "wygasa za 24 godz", "wygasł"). Pure so the dialog list is testable.
 */
export function formatTokenExpiry(expiresAt: string, now: Date = new Date()): string {
	const at = new Date(expiresAt);
	if (Number.isNaN(at.getTime())) return "brak daty";
	const ms = at.getTime() - now.getTime();
	if (ms <= 0) return "wygasł";
	const mins = Math.floor(ms / (1000 * 60));
	const hours = Math.floor(mins / 60);
	const days = Math.floor(hours / 24);
	if (days >= 1) return `wygasa za ${days} dni`;
	if (hours >= 1) return `wygasa za ${hours} godz`;
	return `wygasa za ${Math.max(1, mins)} min`;
}

/**
 * Map a backend `failureReason` code to a human Polish message. Covers the
 * codes the prepare/launch lifecycle can surface; unknown codes fall back to a
 * generic message (the raw code is shown in parentheses for support, never
 * silently swallowed). Pure so it is unit-testable.
 */
export function failureReasonLabel(reason: string | null | undefined): string {
	switch (reason) {
		case "prepare_timeout":
			return "Urządzenie docelowe nie odpowiedziało na czas podczas przygotowania połączenia.";
		case "prepare_failed":
			return "Nie udało się przygotować zdalnego pulpitu na urządzeniu docelowym.";
		case "prepare_dispatch_failed":
			return "Nie udało się wysłać polecenia przygotowania do urządzenia docelowego.";
		case "controller_unreachable":
			return "Urządzenie sterujące jest nieosiągalne.";
		case "target_offline":
			return "Urządzenie docelowe jest offline.";
		case "controller_offline":
			return "Urządzenie sterujące jest offline.";
		case null:
		case undefined:
		case "":
			return "Nieznany błąd połączenia.";
		default:
			return `Błąd połączenia (${reason}).`;
	}
}

/** Roles allowed to act as the controller (the operator's own machine). */
const CONTROLLER_ROLES = new Set<string>(["office_laptop", "it_admin", "natubay"]);

/**
 * A device can drive a remote session (be the controller) when it is online and
 * carries an operator role. Pharmacy workstations / servers are targets, never
 * controllers.
 */
export function isControllerDevice(device: DeviceAgent): boolean {
	return device.status === "online" && CONTROLLER_ROLES.has(device.deviceRole);
}

/**
 * Resolve the controller automatically from the local agent's identity read off
 * `127.0.0.1/whoami` — i.e. „this computer". Matches the local agent to a device
 * row by UUID **or hostname**: after a re-enroll the agent's local identity UUID
 * can diverge from the backend device UUID, but the hostname stays stable, so
 * hostname is the robust fallback. Returns the matched device's UUID only when it
 * is an eligible, online controller AND not the target itself. Any other case →
 * `null` (caller must NOT silently pick another machine). Pure → unit-tested.
 */
export function resolveAutoController(
	local: { deviceUuid?: string | null; hostname?: string | null } | null | undefined,
	devices: DeviceAgent[],
	targetUuid: string,
): string | null {
	const uuid = local?.deviceUuid ?? undefined;
	const host = local?.hostname?.toLowerCase() ?? undefined;
	if (!uuid && !host) return null;
	const match = devices.find(
		(d) =>
			(uuid !== undefined && d.deviceUuid === uuid) ||
			(host !== undefined && d.hostname != null && d.hostname.toLowerCase() === host),
	);
	if (!match || match.deviceUuid === targetUuid) return null;
	return isControllerDevice(match) ? match.deviceUuid : null;
}

// ─── Metryki obciążenia (typowe wykorzystanie) ───────────────────────────────

export type MetricsLevel = "ok" | "warning" | "critical";

/** Progi semantyczne dla wartości % obciążenia (kafel + Diagnostyka). */
export function metricsLevel(pct: number | null | undefined): MetricsLevel | null {
	if (pct == null || !Number.isFinite(pct)) return null;
	if (pct >= 80) return "critical";
	if (pct >= 60) return "warning";
	return "ok";
}

/** "31%" albo "—" gdy kanał niemierzony (null ≠ 0 — zero to realny odczyt). */
export function formatMetricPct(pct: number | null | undefined): string {
	if (pct == null || !Number.isFinite(pct)) return "—";
	return `${Math.round(pct)}%`;
}

/**
 * Czy trójka metryk ma cokolwiek do pokazania? Wszystkie kanały null (stary
 * agent / zero okien w DB) → sekcję chowamy zamiast renderować "— · — · —".
 */
export function hasAnyMetric(triple: MetricsTriple | null | undefined): boolean {
	if (!triple) return false;
	return triple.cpuPct != null || triple.ramPct != null || triple.diskBusyPct != null;
}

/**
 * Konwersja popularnych emotikonów ASCII na emoji — stosowana przy WYSYŁCE
 * notatki (frontend), więc backend zapisuje już prawdziwe emoji, a helper na
 * hoście renderuje je natywnie (Segoe UI Emoji), bez zmian w agencie. Token
 * musi być samodzielny (otoczony spacjami), żeby nie psuć słów/ścieżek (np.
 * „C:\…", „godz:") — zamieniamy tylko całe tokeny.
 */
const EMOTICON_MAP: Record<string, string> = {
	":)": "🙂",
	":-)": "🙂",
	":(": "🙁",
	":-(": "🙁",
	":D": "😀",
	":-D": "😀",
	";)": "😉",
	";-)": "😉",
	":P": "😛",
	":-P": "😛",
	":p": "😛",
	":o": "😮",
	":O": "😮",
	":|": "😐",
	":*": "😘",
	":'(": "😢",
	XD: "😆",
	xD: "😆",
	"<3": "❤️",
};

export function emojifyEmoticons(text: string): string {
	// Split z zachowaniem białych znaków (grupa przechwytująca) → mapujemy tylko
	// nie-białe tokeny.
	return text
		.split(/(\s+)/)
		.map((tok) => EMOTICON_MAP[tok] ?? tok)
		.join("");
}
